import { useState, useEffect, useCallback } from 'react';
import { useSuperAdmin } from '../../context/SuperAdminContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../../components/ui/alert-dialog';
import { 
  Monitor, 
  Smartphone, 
  Search, 
  RefreshCw, 
  Trash2, 
  Users, 
  Building2,
  Clock,
  Globe,
  Shield,
  UserCog,
  Activity,
  AlertTriangle,
  LogOut
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const SessionsManagement = () => {
  const { api } = useSuperAdmin();
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [clientFilter, setClientFilter] = useState('all');
  const [refreshing, setRefreshing] = useState(false);

  // Super Admin sessions state
  const [saSessions, setSaSessions] = useState([]);
  const [saLoading, setSaLoading] = useState(true);

  const fetchSaSessions = useCallback(async () => {
    try {
      const res = await api.get('/api/super-admin/sa-sessions');
      setSaSessions(res.data);
    } catch { /* silently fail — not critical */ }
    finally { setSaLoading(false); }
  }, [api]);

  const handleTerminateSaSession = async (sessionId, adminEmail) => {
    try {
      await api.post(`/api/super-admin/sa-sessions/${sessionId}/terminate`);
      toast.success(`Session terminated for ${adminEmail}`);
      fetchSaSessions();
    } catch (error) {
      toast.error('Failed to terminate admin session');
    }
  };

  const fetchSessions = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (clientFilter && clientFilter !== 'all') {
        params.append('client_id', clientFilter);
      }
      params.append('active_only', 'true');
      
      const [sessionsRes, statsRes] = await Promise.all([
        api.get(`/api/super-admin/sessions?${params.toString()}`),
        api.get('/api/super-admin/sessions/stats')
      ]);
      setSessions(sessionsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to load sessions');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [api, clientFilter]);

  useEffect(() => {
    fetchSessions();
    fetchSaSessions();
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => { fetchSessions(); fetchSaSessions(); }, 30000);
    return () => clearInterval(interval);
  }, [fetchSessions, fetchSaSessions]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchSessions();
    fetchSaSessions();
  };

  const handleTerminateSession = async (sessionId) => {
    try {
      await api.delete(`/api/super-admin/sessions/${sessionId}`);
      toast.success('Session terminated');
      fetchSessions();
    } catch (error) {
      toast.error('Failed to terminate session');
    }
  };

  const handleTerminateUserSessions = async (userId, userName) => {
    try {
      await api.delete(`/api/super-admin/sessions/user/${userId}`);
      toast.success(`All sessions terminated for ${userName}`);
      fetchSessions();
    } catch (error) {
      toast.error('Failed to terminate sessions');
    }
  };

  const handleTerminateTenantSessions = async (clientId) => {
    try {
      await api.delete(`/api/super-admin/sessions/tenant/${clientId}`);
      toast.success(`All sessions terminated for tenant ${clientId}`);
      fetchSessions();
    } catch (error) {
      toast.error('Failed to terminate sessions');
    }
  };

  const getDeviceIcon = (deviceInfo) => {
    if (!deviceInfo) return <Monitor className="h-4 w-4" />;
    const lower = deviceInfo.toLowerCase();
    if (lower.includes('mobile') || lower.includes('android') || lower.includes('ios')) {
      return <Smartphone className="h-4 w-4" />;
    }
    return <Monitor className="h-4 w-4" />;
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'admin':
        return <Badge className="bg-purple-100 text-purple-800"><Shield className="h-3 w-3 mr-1" />Admin</Badge>;
      case 'dispatcher':
        return <Badge className="bg-blue-100 text-blue-800"><UserCog className="h-3 w-3 mr-1" />Dispatcher</Badge>;
      default:
        return <Badge variant="secondary">{role}</Badge>;
    }
  };

  const filteredSessions = sessions.filter(s => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      s.user_name?.toLowerCase().includes(term) ||
      s.client_id?.toLowerCase().includes(term) ||
      s.ip_address?.includes(term) ||
      s.device_info?.toLowerCase().includes(term)
    );
  });

  // Get unique client IDs for filter
  const uniqueClients = [...new Set(sessions.map(s => s.client_id))].sort();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold">Session Management</h1>
          <p className="text-muted-foreground">Monitor and manage active user sessions across all tenants</p>
        </div>
        <Button onClick={handleRefresh} variant="outline" disabled={refreshing}>
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Super Admin Sessions */}
      <Card data-testid="sa-sessions-card" className="border-amber-500/30 bg-amber-500/5">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-amber-500" />
              <CardTitle className="text-lg">Admin Portal Sessions</CardTitle>
              <Badge variant="outline" className="ml-2 border-amber-500/50 text-amber-600">
                {saSessions.length} active
              </Badge>
            </div>
          </div>
          <CardDescription>Active Super Admin sessions — force-terminate suspicious activity</CardDescription>
        </CardHeader>
        <CardContent>
          {saLoading ? (
            <div className="animate-pulse space-y-2">
              {[1, 2].map(i => <div key={`sa-skel-${i}`} className="h-12 bg-muted rounded" />)}
            </div>
          ) : saSessions.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No active admin sessions</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Admin</TableHead>
                  <TableHead>Device</TableHead>
                  <TableHead>IP Address</TableHead>
                  <TableHead>Logged In</TableHead>
                  <TableHead>Last Active</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {saSessions.map((s) => {
                  const loginTime = s.login_at ? new Date(s.login_at) : null;
                  const lastActive = s.last_activity ? new Date(s.last_activity) : null;
                  const isRecent = lastActive && (Date.now() - lastActive.getTime()) < 5 * 60 * 1000;
                  return (
                    <TableRow key={s.id} data-testid={`sa-session-row-${s.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className={`h-2 w-2 rounded-full ${isRecent ? 'bg-green-500' : 'bg-gray-400'}`} />
                          <div>
                            <p className="font-medium">{s.admin_name}</p>
                            <p className="text-xs text-muted-foreground">{s.admin_email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Monitor className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">{s.device_info || 'Unknown'}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="font-mono text-sm">{s.ip_address?.split(',')[0] || '-'}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{loginTime ? formatDistanceToNow(loginTime, { addSuffix: true }) : '-'}</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          {isRecent && <Activity className="h-3 w-3 text-green-500" />}
                          <span className="text-sm">{lastActive ? formatDistanceToNow(lastActive, { addSuffix: true }) : '-'}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:text-destructive"
                              data-testid={`terminate-sa-session-${s.id}`}
                            >
                              <LogOut className="h-4 w-4 mr-1" />
                              Force Logout
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Terminate Admin Session</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will immediately log out <strong>{s.admin_name}</strong> ({s.admin_email}).
                                They will need to sign in again.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleTerminateSaSession(s.id, s.admin_email)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                Terminate Session
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Sessions</p>
                  <p className="text-3xl font-bold">{stats.total_active_sessions}</p>
                </div>
                <Activity className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Admins Online</p>
                  <p className="text-3xl font-bold">{stats.sessions_by_role?.admin || 0}</p>
                </div>
                <Shield className="h-8 w-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Dispatchers Online</p>
                  <p className="text-3xl font-bold">{stats.sessions_by_role?.dispatcher || 0}</p>
                </div>
                <UserCog className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Sessions by Tenant */}
      {stats?.sessions_by_tenant?.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Sessions by Tenant</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {stats.sessions_by_tenant.map((t) => (
                <div key={t.client_id} className="flex items-center gap-2 px-3 py-1.5 bg-muted rounded-md">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <span className="font-mono text-sm">{t.client_id}</span>
                  <Badge variant="secondary">{t.count}</Badge>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-destructive hover:text-destructive">
                        <LogOut className="h-3 w-3" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Terminate All Tenant Sessions</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will log out ALL users from tenant <strong>{t.client_id}</strong>. 
                          They will need to log in again. Are you sure?
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction 
                          onClick={() => handleTerminateTenantSessions(t.client_id)}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          Terminate All
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Active Sessions ({filteredSessions.length})</CardTitle>
            <div className="flex items-center gap-3">
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by user, IP, device..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select value={clientFilter} onValueChange={setClientFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="All Tenants" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tenants</SelectItem>
                  {uniqueClients.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="animate-pulse space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={`skeleton-${i}`} className="h-16 bg-muted rounded" />
              ))}
            </div>
          ) : filteredSessions.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No active sessions</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Tenant</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Device</TableHead>
                  <TableHead>IP Address</TableHead>
                  <TableHead>Last Activity</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSessions.map((session) => (
                  <TableRow key={session.id}>
                    <TableCell className="font-medium">{session.user_name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-mono">{session.client_id}</Badge>
                    </TableCell>
                    <TableCell>{getRoleBadge(session.user_role)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getDeviceIcon(session.device_info)}
                        <span className="text-sm text-muted-foreground">{session.device_info || 'Unknown'}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Globe className="h-4 w-4 text-muted-foreground" />
                        <span className="font-mono text-sm">{session.ip_address || '-'}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 text-sm">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        {formatDistanceToNow(new Date(session.last_activity), { addSuffix: true })}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Terminate Session</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will log out <strong>{session.user_name}</strong> from their current session.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction 
                                onClick={() => handleTerminateSession(session.id)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                Terminate
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                        
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm" className="text-orange-600 hover:text-orange-600">
                              <LogOut className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Terminate All User Sessions</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will log out <strong>{session.user_name}</strong> from ALL their active sessions.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction 
                                onClick={() => handleTerminateUserSessions(session.user_id, session.user_name)}
                                className="bg-orange-600 text-white hover:bg-orange-700"
                              >
                                Terminate All
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Info Note */}
      <Card className="bg-muted/50">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">Session Timeout Policy</p>
              <p className="text-sm text-muted-foreground mt-1">
                Sessions automatically expire after 30 minutes of inactivity. 
                Users will see a simple message asking them to log in again.
                This list auto-refreshes every 30 seconds.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SessionsManagement;
